import { parseEventDate, formatEventTime } from '../utils/date-helpers.js';
import { escapeHtml } from '../utils/html-utils.js';
import { readTabFromUrl, writeTabToUrl } from '../utils/tab-url.js';

const EVENTS_CALENDAR_DEFAULT_TAB = 'my';

// Events Calendar Component with Month and Weekly views
class EventsCalendar extends HTMLElement {
  constructor() {
    super();
    this.currentView = 'month'; // 'month' or 'week'
    this.currentTab = 'my'; // 'my' or 'all'
    this.currentDate = new Date(); // Current month/week being viewed
    this.events = [];
    this.loading = true;
    this.showPublic = false; // Toggle for public events in "All Events"
  }

  async connectedCallback() {
    this.currentTab = readTabFromUrl(['my', 'all'], { defaultTab: EVENTS_CALENDAR_DEFAULT_TAB });
    await this.loadEvents();
    this.render();
  }

  async loadEvents() {
    try {
      this.loading = true;

      // Calculate date range based on current view
      let startDate, endDate;
      if (this.currentView === 'month') {
        // Load entire month
        startDate = dayjs(this.currentDate).startOf('month').toDate();
        endDate = dayjs(this.currentDate).endOf('month').toDate();
      } else {
        // Load entire week
        startDate = dayjs(this.currentDate).startOf('week').toDate();
        endDate = dayjs(this.currentDate).endOf('week').toDate();
      }

      const queryParams = new URLSearchParams({
        startDate: startDate.toISOString(),
        endDate: endDate.toISOString()
      });

      // Load my events for the current view range
      const response = await window.API.request(`/events?${queryParams}`);

      // Combine upcoming and past events into single array
      this.events = [
        ...(response.upcoming_events || []),
        ...(response.past_events || [])
      ];

      this.loading = false;
      this.render();
    } catch (error) {
      window.logger.error('Failed to load events:', error);
      this.loading = false;
      this.render();
    }
  }

  render() {
    if (this.loading) {
      this.innerHTML = `
        <div class="calendar-container">
          <div class="loading">Loading calendar...</div>
        </div>
      `;
      return;
    }

    this.innerHTML = `
      <div class="calendar-container">
        ${this.renderHeader()}
        ${this.renderTabs()}
        ${this.renderView()}
      </div>
    `;

    this.attachEventListeners();
  }

  renderHeader() {
    return `
      <div class="calendar-header">
        <h1>Calendar</h1>
        <div class="calendar-controls">
          <div class="header-left">

          </div>
          <div class="header-right">
            <a href="/events" class="btn-view-list">📋 List View</a>
            <button class="btn-create-event" id="createEventBtn">
              🎉 Create Event
            </button>
          </div>
        </div>
      </div>
      <toolbar-action-bar label="Calendar view">
        <div slot="actions" class="actions">
            <div class="view-switcher">
              <button class="view-btn ${this.currentView === 'month' ? 'active' : ''}" data-view="month">
                Month
              </button>
              <button class="view-btn ${this.currentView === 'week' ? 'active' : ''}" data-view="week">
                Week
              </button>
            </div>
            ${this.renderNavigation()}
        </div>
      </toolbar-action-bar>
    `;
  }

  renderTabs() {
    return `
      <div class="events-tabs">
        <button class="tab-btn ${this.currentTab === 'my' ? 'active' : ''}" data-tab="my">
          My Events
        </button>
        <button class="tab-btn ${this.currentTab === 'all' ? 'active' : ''}" data-tab="all">
          All Events
        </button>
        ${this.currentTab === 'all' ? `
          <label class="toggle-checkbox-label" id="publicToggleLabel">
            <input type="checkbox" class="toggle-checkbox" id="showPublicCheckbox" ${this.showPublic ? 'checked' : ''}>
            <span class="toggle-checkbox-custom"></span>
            <span class="toggle-label-text">Show Public Events</span>
          </label>
        ` : ''}
      </div>
    `;
  }

  renderNavigation() {
    if (this.currentView === 'month') {
      const monthYear = dayjs(this.currentDate).format('MMMM YYYY');
      return `
        <div class="calendar-nav">
          <button class="nav-btn" id="prevBtn">
            ‹
          </button>
          <span class="current-period">${monthYear}</span>
          <button class="nav-btn" id="nextBtn">
            ›
          </button>
        </div>
      `;
    } else if (this.currentView === 'week') {
      const weekStart = dayjs(this.currentDate).startOf('week');
      const weekEnd = dayjs(this.currentDate).endOf('week');
      const weekStr = weekStart.month() === weekEnd.month()
        ? `${weekStart.format('MMM D')} - ${weekEnd.format('D, YYYY')}`
        : `${weekStart.format('MMM D')} - ${weekEnd.format('MMM D, YYYY')}`;
      return `
        <div class="calendar-nav">
          <button class="nav-btn" id="prevBtn">
            ‹
          </button>
          <span class="current-period">${weekStr}</span>
          <button class="nav-btn" id="nextBtn">
            ›
          </button>
        </div>
      `;
    }
    return '';
  }

  renderView() {
    switch (this.currentView) {
    case 'month':
      return this.renderMonthView();
    case 'week':
      return this.renderWeekView();
    default:
      return this.renderMonthView();
    }
  }

  renderMonthView() {
    // Get first day of month and days in month
    const startOfMonth = dayjs(this.currentDate).startOf('month');
    const endOfMonth = dayjs(this.currentDate).endOf('month');
    const daysInMonth = endOfMonth.date();
    const firstDayOfWeek = startOfMonth.day(); // 0 = Sunday, 6 = Saturday

    // Filter events based on tab
    const displayEvents = this.getFilteredEvents();
    const monthEvents = this.getEventsInRange(displayEvents, startOfMonth.toDate(), endOfMonth.toDate());

    // Group events by date for display (using event timezone to keep original date)
    const eventsByDate = {};
    monthEvents.forEach(event => {
      const parsedDate = parseEventDate(event.start_time, event.timezone || 'UTC');
      if (parsedDate) {
        const eventDay = typeof dayjs?.tz === 'function'
          ? dayjs(parsedDate).tz(event.timezone || 'UTC')
          : dayjs(parsedDate);
        const eventDate = eventDay.format('YYYY-MM-DD');
        if (!eventsByDate[eventDate]) {
          eventsByDate[eventDate] = [];
        }
        eventsByDate[eventDate].push(event);
      }
    });

    // Build calendar grid
    const days = [];
    const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

    // Add empty cells for days before the first of the month
    for (let i = 0; i < firstDayOfWeek; i++) {
      days.push('<div class="calendar-day empty"></div>');
    }

    // Add cells for each day of the month
    for (let day = 1; day <= daysInMonth; day++) {
      const dateStr = dayjs(this.currentDate).date(day).format('YYYY-MM-DD');
      const isToday = dayjs(this.currentDate).date(day).isSame(dayjs(), 'day');
      const dayEvents = eventsByDate[dateStr] || [];

      days.push(`
        <div class="calendar-day ${isToday ? 'today' : ''}" data-date="${dateStr}">
          <div class="day-number">${day}</div>
          <div class="day-events">
            ${dayEvents.slice(0, 3).map(event => this.renderEventDot(event)).join('')}
            ${dayEvents.length > 3 ? `<div class="event-dot more">+${dayEvents.length - 3}</div>` : ''}
          </div>
        </div>
      `);
    }

    return `
      <div class="calendar-view">
        <div class="calendar-grid">
          ${dayNames.map(day => `<div class="day-name">${day}</div>`).join('')}
          ${days.join('')}
        </div>
      </div>
    `;
  }

  renderWeekView() {
    const weekStart = dayjs(this.currentDate).startOf('week');
    const weekEnd = dayjs(this.currentDate).endOf('week');

    // Filter events based on tab
    const displayEvents = this.getFilteredEvents();
    const weekEvents = this.getEventsInRange(displayEvents, weekStart.toDate(), weekEnd.toDate());

    // Group events by date (respecting event timezone to avoid shifting days)
    const eventsByDate = {};
    weekEvents.forEach(event => {
      const parsedDate = parseEventDate(event.start_time, event.timezone || 'UTC');
      if (parsedDate) {
        const eventDay = typeof dayjs?.tz === 'function'
          ? dayjs(parsedDate).tz(event.timezone || 'UTC')
          : dayjs(parsedDate);
        const eventDate = eventDay.format('YYYY-MM-DD');
        if (!eventsByDate[eventDate]) {
          eventsByDate[eventDate] = [];
        }
        eventsByDate[eventDate].push(event);
      }
    });

    // Create day sections
    const daySections = [];
    for (let i = 0; i < 7; i++) {
      const day = weekStart.add(i, 'day');
      const dateStr = day.format('YYYY-MM-DD');
      const dayEvents = eventsByDate[dateStr] || [];
      const isToday = day.isSame(dayjs(), 'day');

      daySections.push(`
        <div class="week-day ${isToday ? 'today' : ''}">
          <div class="week-day-header">
            <div class="week-day-name">${day.format('ddd')}</div>
            <div class="week-day-number">${day.format('D')}</div>
          </div>
          <div class="week-day-events">
            ${dayEvents.length > 0
    ? dayEvents.map(event => this.renderEventCard(event)).join('')
    : '<div class="empty-day">No events</div>'
}
          </div>
        </div>
      `);
    }

    return `
      <div class="calendar-view">
        <div class="week-view">
          ${daySections.join('')}
        </div>
      </div>
    `;
  }

  renderEventDot(event) {
    const cancelledClass = event.is_cancelled ? 'cancelled' : '';
    const rsvpClass = event.is_cancelled ? '' : this.getRSVPClass(event);
    const eventId = event.id;
    return `
      <div class="event-dot ${cancelledClass} ${rsvpClass}" 
           title="${escapeHtml(event.title)}${event.is_cancelled ? ' (Cancelled)' : ''}"
           data-event-id="${event.id}"
           onclick="event.stopPropagation(); window.location = '/events/${eventId}'">
        ${event.is_cancelled ? '<span class="cancelled-label">Cancelled</span> ' : ''}${escapeHtml(event.title).substring(0, 15)}${event.title.length > 15 ? '...' : ''}
      </div>
    `;
  }

  renderEventCard(event) {
    const startDate = parseEventDate(event.start_time, event.timezone || 'UTC');
    const startTimeStr = formatEventTime(startDate);
    const isPast = startDate < new Date();
    const cancelledClass = event.is_cancelled ? 'cancelled' : '';
    const rsvpClass = event.is_cancelled ? '' : this.getRSVPClass(event);
    const eventId = event.id;

    return `
      <div data-event-id="${eventId}" class="event-card ${isPast ? 'past' : ''} ${cancelledClass} ${rsvpClass}">
        <div class="event-time">${startTimeStr}</div>
        <div class="event-info">
          <h4>${event.is_cancelled ? '<span class="cancelled-label">Cancelled</span> ' : ''}${escapeHtml(event.title)}</h4>
          ${event.location ? `<div class="event-location">📍 ${escapeHtml(event.location)}</div>` : ''}
        </div>
      </div>
    `;
  }

  getRSVPClass(event) {
    if (!event.user_rsvp) {
      return 'rsvp-none';
    }
    return `rsvp-${event.user_rsvp.toLowerCase()}`;
  }

  getFilteredEvents() {
    // TODO: Filter based on "My Events" vs "All Events" and public toggle
    // For now, just return all events
    return this.events;
  }

  getEventsInRange(events, startDate, endDate) {
    return events.filter(event => {
      const eventDate = parseEventDate(event.start_time, event.timezone || 'UTC');
      return eventDate >= startDate && eventDate <= endDate;
    });
  }


  attachEventListeners() {
    // View switcher
    const viewBtns = this.querySelectorAll('.view-btn');
    viewBtns.forEach(btn => {
      btn.addEventListener('click', async (e) => {
        this.currentView = e.target.dataset.view;
        this.loadEvents();
      });
    });

    // Tab switcher
    const tabBtns = this.querySelectorAll('.tab-btn');
    tabBtns.forEach(btn => {
      btn.addEventListener('click', (e) => {
        this.currentTab = e.target.dataset.tab;
        writeTabToUrl(this.currentTab, { omitDefault: EVENTS_CALENDAR_DEFAULT_TAB });
        this.render();
      });
    });

    // Public events toggle
    const publicCheckbox = this.querySelector('#showPublicCheckbox');
    if (publicCheckbox) {
      publicCheckbox.addEventListener('change', (e) => {
        this.showPublic = e.target.checked;
        this.render();
      });
    }

    // Navigation
    const prevBtn = this.querySelector('#prevBtn');
    const nextBtn = this.querySelector('#nextBtn');
    if (prevBtn) {
      prevBtn.addEventListener('click', async () => {
        if (this.currentView === 'month') {
          this.currentDate = dayjs(this.currentDate).subtract(1, 'month').toDate();
        } else {
          this.currentDate = dayjs(this.currentDate).subtract(1, 'week').toDate();
        }
        await this.loadEvents();
      });
    }
    if (nextBtn) {
      nextBtn.addEventListener('click', async () => {
        if (this.currentView === 'month') {
          this.currentDate = dayjs(this.currentDate).add(1, 'month').toDate();
        } else {
          this.currentDate = dayjs(this.currentDate).add(1, 'week').toDate();
        }
        await this.loadEvents();
      });
    }

    // Create event buttons
    const createEventBtns = this.querySelectorAll('#createEventBtn, #createEventBtnEmpty');
    createEventBtns.forEach(btn => {
      btn.addEventListener('click', () => {
        window.location = '/events/new';
      });
    });

    // Click on month calendar day to switch to week view
    const calendarDays = this.querySelectorAll('.calendar-day:not(.empty)');
    calendarDays.forEach(day => {
      day.addEventListener('click', (e) => {
        const dateStr = e.currentTarget.dataset.date;
        if (dateStr) {
          // Use dayjs to parse date properly to avoid timezone issues
          this.currentDate = dayjs(dateStr).toDate();
          this.currentView = 'week';
          this.loadEvents();
        }
      });
    });

    const events = document.querySelectorAll('[data-event-id]');
    if (events) {
      events.forEach(event => {
        const eventId = event.dataset.eventId;
        event.addEventListener('click', async (e) => {
          e.stopImmediatePropagation();
          window.location = `/events/${eventId}`;
        });
      });
    }
  }
}

customElements.define('events-calendar', EventsCalendar);
export default EventsCalendar;
